// API utility with axios for authenticated requests
import axios from 'axios';

const defaultApiBaseUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:8081';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:8081';
  return window.location.origin;
};

const isCompanyHost = typeof window !== 'undefined' && window.location.hostname.endsWith('.doorbase.uk');
export const API_BASE_URL = isCompanyHost ? window.location.origin : (process.env.REACT_APP_API_URL || defaultApiBaseUrl());

export function apiPath(url = '') {
  return url;
}

export function apiUrl(url = '') {
  const normalized = apiPath(url);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  const path = normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${API_BASE_URL}${path}`;
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to add token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('doorbase_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle 401
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('doorbase_token');
      localStorage.removeItem('doorbase_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Wrapper to match existing fetch-style API
export async function apiFetch(url, options = {}) {
  try {
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers = { ...(options.headers || {}) };
    if (isFormData) {
      delete headers['content-type'];
      headers['Content-Type'] = undefined;
    }

    const response = await apiClient.request({
      url: apiPath(url),
      method: options.method || 'GET',
      data: isFormData ? options.body : (options.body ? JSON.parse(options.body) : undefined),
      headers
    });

    // Return a fetch-compatible response
    return {
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      json: async () => response.data
    };
  } catch (error) {
    if (error.response) {
      const data = error.response.data || {};
      const failure = new Error(data.error || data.message || `Server error (${error.response.status})`);
      failure.response = error.response;
      throw failure;
    }
    throw error;
  }
}

// Helper functions
export async function apiGet(url) {
  const response = await apiFetch(url);
  return response.json();
}

export async function apiPost(url, data) {
  const response = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
  return response.json();
}

export async function apiPut(url, data) {
  const response = await apiFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
  return response.json();
}

export async function apiDelete(url) {
  const response = await apiFetch(url, {
    method: 'DELETE'
  });
  return response.json();
}

export default apiFetch;
